import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { Copy, Check } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { PasswordField } from "@/shared/components/ui/password-field";
import { useToast } from "@/shared/hooks/use-toast";
import type { AuthField, FormField } from "../types/integration";

interface IntegrationConfigFieldsProps {
  /** `settings.form_fields` do template. */
  settingsFields: FormField[];
  /** `auth_fields.fields` do template. */
  authFields: AuthField[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  /**
   * Título "Configurações" acima dos campos. O diálogo o esconde quando já está numa aba
   * com esse nome — repetiria o nome da própria aba (AB#85605).
   */
  showSettingsHeading?: boolean;
  /** Texto do campo de credencial na edição, onde vazio = manter a atual (AB#87542). */
  credentialHint?: string;
}

/**
 * Os campos que o TEMPLATE declara — configurações primeiro, autenticação por último.
 *
 * Saiu do `IntegrationsForm` quando a edição virou página (AB#87542): o diálogo de criar e
 * a aba Conexão renderizam a mesma coisa, e duas cópias divergiriam com o tempo.
 */
export const IntegrationConfigFields = ({
  settingsFields,
  authFields,
  values,
  onChange,
  showSettingsHeading = true,
  credentialHint,
}: IntegrationConfigFieldsProps) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  // Prefixo dos ids que ligam cada rótulo ao seu campo; único por formulário na página.
  const idPrefix = useId();
  const { toast } = useToast();
  // Um timer por vez, e nenhum depois de desmontar: o "copiado" não pode tentar atualizar um
  // componente que já saiu da tela.
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  // O navegador pode negar a área de transferência (permissão, contexto sem HTTPS): sem o
  // aviso, o botão pareceria quebrado.
  const handleCopyField = async (fieldKey: string, value: string) => {
    try {
      if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldKey);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast({ title: t("integrations.messages.copyFailed"), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      {/* Campo honeypot invisível para confundir autocomplete */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        type="password"
        name="password"
        autoComplete="current-password"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Campos de Configurações - apresentados primeiro */}
      {settingsFields.length > 0 && (
        <div className="space-y-4">
          {showSettingsHeading && (
            <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">
              {t('integrations.sections.settings')}
            </h3>
          )}
          {settingsFields.map((field) => {
            const fieldKey = field.key || field.name;
            const inputId = `${idPrefix}-setting-${fieldKey}`;
            return (
              <div key={fieldKey} className="space-y-2">
                <label htmlFor={inputId} className="text-sm font-medium">
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </label>
                {field.description && (
                  <p
                    className="text-xs text-muted-foreground [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                  />
                )}

                {field.readonly ? (
                  <div className="flex items-center gap-2">
                    {/* Cai no default do template: campo travado não é editável,
                        então até o primeiro save não há valor na instância — e sem
                        isto a pessoa vê um campo obrigatório em branco. O submit já
                        grava `field.default` com a mesma expressão. */}
                    <Input
                      id={inputId}
                      type="text"
                      value={String(values[fieldKey] || field.default || '')}
                      readOnly
                      className="font-mono text-xs bg-muted text-muted-foreground cursor-default select-all"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0 h-9 w-9"
                      onClick={() => void handleCopyField(fieldKey, String(values[fieldKey] || field.default || ''))}
                      title={t('common.copy', 'Copiar')}
                    >
                      {copiedField === fieldKey ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                ) : field.type === 'password' ? (
                  <PasswordField
                    id={inputId}
                    field={{ ...field, name: fieldKey }}
                    value={String(values[fieldKey] || '')}
                    onChange={(value) => onChange(fieldKey, value)}
                  />
                ) : field.type === 'select' ? (
                  <select
                    id={inputId}
                    value={String(values[fieldKey] || '')}
                    onChange={(e) => onChange(fieldKey, e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                    required={field.required}
                  >
                    <option value="">{t('integrations.placeholders.select')}</option>
                    {field.options?.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : field.type === 'checkbox' ? (
                  <div className="flex items-center space-x-2">
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={Boolean(values[fieldKey])}
                      onChange={(e) => onChange(fieldKey, e.target.checked)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                  </div>
                ) : (
                  // Configuração não é credencial: campo comum, sem o truque de nascer travado.
                  <Input
                    id={inputId}
                    type={field.type || 'text'}
                    placeholder={field.placeholder || t('integrations.placeholders.enterField', { field: field.label.toLowerCase() })}
                    value={String(values[fieldKey] || '')}
                    onChange={(e) => onChange(fieldKey, e.target.value)}
                    required={field.required}
                    autoComplete="off"
                    name={`field_${fieldKey}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Campos de Autenticação - apresentados por último */}
      {authFields.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground border-b pb-2">{t('integrations.sections.authentication')}</h3>
          {credentialHint && <p className="text-xs text-muted-foreground">{credentialHint}</p>}
          {authFields.map((field) => (
            <div key={field.key} className="space-y-2">
              <label htmlFor={`${idPrefix}-auth-${field.key}`} className="text-sm font-medium">
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </label>
              {field.description && (
                <p
                  className="text-xs text-muted-foreground [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                />
              )}

              {field.type === 'password' || field.secret ? (
                <PasswordField
                  id={`${idPrefix}-auth-${field.key}`}
                  field={{ name: field.key, ...field }}
                  value={String(values[field.key] || '')}
                  onChange={(value) => onChange(field.key, value)}
                />
              ) : field.type === 'select' ? (
                <select
                  id={`${idPrefix}-auth-${field.key}`}
                  value={String(values[field.key] || '')}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  required={field.required}
                  autoComplete="new-password"
                  name={`select_${field.key}`}
                >
                  <option value="">{t('integrations.placeholders.select')}</option>
                  {field.options?.map((option: string) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                // Campo de credencial (usuário, e-mail, conta): nasce travado e destrava no foco
                // porque o navegador ignora `autoComplete` e preencheria o login salvo do Prodgy
                // na credencial do fornecedor. Só aqui — configuração comum não precisa disso.
                <Input
                  id={`${idPrefix}-auth-${field.key}`}
                  type={field.type || 'text'}
                  placeholder={t('integrations.placeholders.enterField', { field: field.label.toLowerCase() })}
                  value={String(values[field.key] || '')}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  required={field.required}
                  autoComplete="new-password"
                  name={`auth_${field.key}`}
                  readOnly
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
